// #519 — scribe sign-out.
//
// The bug: the old handler only redirected to the Cognito hosted-UI /logout
// endpoint (clears the pool cookie) but never dropped the OIDC user that
// react-oidc-context keeps in web storage. So the post-logout redirect landed
// back in the app still "authenticated" — and, because #480 persists the
// scribe show/position session across the round-trip, on the same show —
// making Sign out look like a no-op.
//
// Full sign-out must therefore (1) removeUser() to purge the stored OIDC
// user/token, (2) clear the persisted scribe session (#480) + resume route,
// then (3) redirect to the pool /logout so the next visit starts clean at the
// login screen with no session anywhere.
import { beginSignOut, isCypressTestMode } from "common-fe";
import { clearPersistedSession } from "../context/ScoreContext";
import { clearPositionChoices } from "../util/positionChoice";
import { RESUME_ROUTE_KEY } from "../hooks/useInactivityRedirect";
import { clearLastIdentity } from "./lastIdentity";

// #1982 — the default redirect below is normally synchronous with no
// intervening browser paint, which is exactly why the Cypress regression in
// logout.cy.js could not mutation-fail: reverting the AuthGate fix still left
// the test green, because the real cross-origin navigation started before
// Cypress's DOM polling ever got a look at the (buggy) fatal screen. A spec
// that deliberately wants that transient window observable opts in with
// `window.__CYPRESS_SIGNOUT_REDIRECT_DELAY_MS__ = <ms>` before clicking Sign
// out; gated on isCypressTestMode() (window.Cypress) so it can never affect a
// real user's sign-out.
function defaultRedirect(url) {
  if (
    isCypressTestMode() &&
    typeof window !== "undefined" &&
    window.__CYPRESS_SIGNOUT_REDIRECT_DELAY_MS__
  ) {
    setTimeout(() => {
      window.location.replace(url);
    }, window.__CYPRESS_SIGNOUT_REDIRECT_DELAY_MS__);
    return;
  }
  window.location.replace(url);
}

// Build the Cognito hosted-UI logout URL (matches manager-fe). Returns null if
// the pool isn't configured, in which case we skip the redirect.
export function cognitoLogoutUrl(env = import.meta.env) {
  const clientId = env.VITE_COGNITO_CLIENT_ID;
  const cognitoDomain = env.VITE_COGNITO_AUTH_DOMAIN;
  const logoutUri = env.VITE_COGNITO_POST_LOGOUT_REDIRECT_URI;
  if (!clientId || !cognitoDomain || !logoutUri) return null;
  return `https://${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(
    logoutUri
  )}`;
}

/**
 * Fully end the scribe session.
 * @param removeUser react-oidc-context's removeUser (purges the stored user).
 * @param redirect   navigation seam (default window.location.replace) — injected
 *                   so it can be exercised without a real page navigation.
 */
export async function fullSignOut(
  removeUser,
  { redirect = defaultRedirect } = {}
) {
  // Latch BEFORE removeUser() so AuthGate's auto-login effect doesn't re-auth
  // the user in the window before the Cognito /logout redirect lands (#519).
  beginSignOut();
  try {
    await removeUser();
  } catch {
    // Even if the local user store can't be cleared, still end the pool session.
  }
  clearPersistedSession();
  // #2090: the per-class seat pick must not outlive the scribe who made it.
  clearPositionChoices();
  // #3256: a deliberate sign-out leaves no cached identity for the next person.
  clearLastIdentity();
  try {
    sessionStorage.removeItem(RESUME_ROUTE_KEY);
  } catch {
    // Storage may be unavailable (private browsing, etc.) — non-fatal.
  }
  const url = cognitoLogoutUrl();
  if (url) redirect(url);
}
